import { useState, useCallback } from "react";
import { AffectedOrder } from "../Domain/AffectedOrder";
import { fetchAffectedOrders, useUpdateClientOrders, useSavePrefs } from "../Domain/dietSettingsApi";

interface UseOrderUpdateParams {
  clientId: string;
  onSuccess: () => void;
  onError: (error: Error) => void;
}

interface UseOrderUpdateReturn {
  isLoading: boolean;
  showModal: boolean;
  affectedOrders: AffectedOrder[];
  saveAndCheckOrders: () => Promise<void>;
  confirmOrderUpdate: (lockDate: Date) => Promise<void>;
  cancelOrderUpdate: () => void;
  closeModal: () => void;
}

/**
 * Custom hook to handle the order update workflow
 * Manages saving preferences, fetching affected orders, and updating orders
 */
export const useOrderUpdate = ({
  clientId,
  onSuccess,
  onError,
}: UseOrderUpdateParams): UseOrderUpdateReturn => {
  const [showModal, setShowModal] = useState(false);
  const [affectedOrders, setAffectedOrders] = useState<AffectedOrder[]>([]);
  const [isFetchingOrders, setIsFetchingOrders] = useState(false);

  const { isPending: isSaving, savePrefs } = useSavePrefs(clientId);
  const { updateOrders } = useUpdateClientOrders(clientId);

  const closeModal = useCallback(() => {
    setShowModal(false);
    onSuccess();
  }, [onSuccess]);

  const saveAndCheckOrders = useCallback(async () => {
    try {
      // Step 1: Save diet preferences first
      await savePrefs();

      // Step 2: Fetch affected orders
      setIsFetchingOrders(true);
      const orders = await fetchAffectedOrders(clientId);
      setIsFetchingOrders(false);

      if (orders.length > 0) {
        // Show modal if there are orders to update
        setAffectedOrders(orders);
        setShowModal(true);
      } else {
        // No orders to update, call success immediately
        onSuccess();
      }
    } catch (error) {
      setIsFetchingOrders(false);
      onError(error as Error);
    }
  }, [clientId, savePrefs, onSuccess, onError]);

  const confirmOrderUpdate = useCallback(
    async (lockDate: Date) => {
      try {
        await updateOrders(lockDate);

        // API succeeded - reload affected orders to show updated state
        setIsFetchingOrders(true);
        const updatedOrders = await fetchAffectedOrders(clientId);
        setAffectedOrders(updatedOrders);
        setIsFetchingOrders(false);

        // If no more affected orders, close modal and call success
        if (updatedOrders.length === 0) {
          closeModal();
        }
      } catch (error) {
        setIsFetchingOrders(false);
        throw error; // Let modal handle the error
      }
    },
    [updateOrders, clientId, closeModal]
  );

  const cancelOrderUpdate = useCallback(() => {
    setShowModal(false);
    setIsFetchingOrders(false);
    onSuccess(); // Still call success on cancel as preferences were saved
  }, [onSuccess]);

  return {
    isLoading: isSaving || isFetchingOrders,
    showModal,
    affectedOrders,
    saveAndCheckOrders,
    confirmOrderUpdate,
    cancelOrderUpdate,
    closeModal,
  };
};
